// Agent Entries Tool — read/write numbered agent-entry-NNN.md under /agents and maintain README index
// README: editable body between AGENTS_README_BODY markers; index bullets below ## … Agent Entry Index (tool-owned)

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";
import {
  AGENTS_ROOT,
  README_REL,
  ENTRY_FILENAME_RE,
  MAX_LATEST_OVERVIEW_ENTRIES,
  MAX_RANGE_INCLUSIVE,
  DEFAULT_README_TEMPLATE,
  extractReadmeBody,
  replaceReadmeBody,
  patchReadmeIndexLines,
  normalizeSummaryForIndex,
} from "../../../shared/agents/agentEntriesShared.js";

export class AgentEntriesTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "agentEntriesTool";
  }

  /** Browser and local workspaces both resolve `/agents` via file tools (LightningFS vs Desktop IPC). */
  _supportsAgentEntries() {
    return this.appType === "browser" || this.appType === "local";
  }

  _deps() {
    const readTool = this.toolSystem?.readFileTool;
    const writeTool = this.toolSystem?.writeFileTool;
    const createFolderTool = this.toolSystem?.createFolderTool;
    return { readTool, writeTool, createFolderTool };
  }

  _pad(entryNumber) {
    const n =
      typeof entryNumber === "number"
        ? entryNumber
        : parseInt(String(entryNumber), 10);
    if (!Number.isInteger(n) || n < 1 || n > 999) {
      return null;
    }
    return String(n).padStart(3, "0");
  }

  _entryPath(padded) {
    return `${AGENTS_ROOT}/agent-entry-${padded}.md`;
  }

  /**
   * Ensure /agents exists and README.md exists (create default template if missing).
   */
  async ensureAgentsReadme() {
    const { readTool, writeTool, createFolderTool } = this._deps();
    if (!readTool || !writeTool || !createFolderTool) {
      return this.returnError(
        {},
        "ReadFileTool, WriteFileTool, or CreateFolderTool is not available"
      );
    }

    if (!this._supportsAgentEntries()) {
      return this.returnError(
        {},
        `App type '${this.appType}' is not supported for agent entries`
      );
    }

    await createFolderTool.createFolder(AGENTS_ROOT, true, true);

    const exists = await readTool.checkExists(README_REL);
    if (!exists) {
      const w = await writeTool.writeFile(
        README_REL,
        DEFAULT_README_TEMPLATE,
        true,
        "utf8",
        true
      );
      if (!w.success) {
        return this.returnError(
          { path: README_REL },
          w.message || "Failed to create agents/README.md"
        );
      }
      logger.log(
        { tags: "tools|agent-entries|readme|create", color1: "lime" },
        "Created default agents/README.md"
      );
      return this.returnSuccess({ created: true }, "Created agents/README.md");
    }

    return this.returnSuccess({ created: false }, "agents/README.md exists");
  }

  /**
   * Read only the editable instructions body (between markers, or legacy content before index).
   */
  async readReadmeBody() {
    const ensured = await this.ensureAgentsReadme();
    if (!ensured.success) {
      return ensured;
    }

    const { readTool } = this._deps();
    const readmeRead = await readTool.readFile(README_REL, "utf8");
    if (!readmeRead.success) {
      return this.returnError(
        {},
        readmeRead.message || "Could not read agents/README.md"
      );
    }

    const extracted = extractReadmeBody(readmeRead.data.content);
    if (!extracted.success) {
      return this.returnError({}, extracted.message);
    }

    return this.returnSuccess(
      {
        path: README_REL,
        body: extracted.body,
        legacyFormat: !!extracted.legacy,
      },
      "Read agents README body"
    );
  }

  /**
   * Replace the editable instructions body; preserves index and footer below it.
   */
  async writeReadmeBody(body) {
    if (typeof body !== "string") {
      return this.returnError({}, "body must be a string");
    }

    const ensured = await this.ensureAgentsReadme();
    if (!ensured.success) {
      return ensured;
    }

    const { readTool, writeTool } = this._deps();
    const readmeRead = await readTool.readFile(README_REL, "utf8");
    if (!readmeRead.success) {
      return this.returnError(
        {},
        readmeRead.message || "Could not read agents/README.md"
      );
    }

    const replaced = replaceReadmeBody(readmeRead.data.content, body);
    if (!replaced.success) {
      return this.returnError({}, replaced.message);
    }

    const writeResult = await writeTool.writeFile(
      README_REL,
      replaced.content,
      true,
      "utf8",
      true
    );
    if (!writeResult.success) {
      return this.returnError(
        { path: README_REL },
        writeResult.message || "Failed to write agents/README.md"
      );
    }

    logger.log(
      { tags: "tools|agent-entries|readme|body", color1: "lime" },
      "Updated agents/README.md body region"
    );

    return this.returnSuccess(
      { path: README_REL },
      "Updated agents README instructions body"
    );
  }

  /**
   * Read-only agent slice for get-project-overview. Does not create /agents or README.
   * @param {number} [latestEntryCount=2] — 0 = readme only (if present); max 50.
   * @returns {Promise<Object>} Plain object for merging into project overview.
   */
  async collectAgentContextForOverview(latestEntryCount) {
    let count = 2;
    if (latestEntryCount !== undefined && latestEntryCount !== null) {
      const n =
        typeof latestEntryCount === "number"
          ? latestEntryCount
          : parseInt(String(latestEntryCount), 10);
      if (!Number.isInteger(n) || n < 0 || n > MAX_LATEST_OVERVIEW_ENTRIES) {
        return {
          agentsPresent: false,
          error: `latest_entry_count must be an integer from 0 to ${MAX_LATEST_OVERVIEW_ENTRIES}`,
          readmeBody: null,
          readmeLegacyFormat: false,
          readmeNote: null,
          readmePath: README_REL,
          latestEntryCountRequested: 2,
          entryNumbersIncluded: [],
          entries: [],
        };
      }
      count = n;
    }

    const { readTool } = this._deps();
    const listTool = this.toolSystem?.listDirectoryTool;
    if (!readTool || !listTool) {
      return {
        agentsPresent: false,
        error: "ReadFileTool or ListDirectoryTool is not available",
        readmeBody: null,
        readmeLegacyFormat: false,
        readmeNote: null,
        readmePath: README_REL,
        latestEntryCountRequested: count,
        entryNumbersIncluded: [],
        entries: [],
      };
    }

    if (!this._supportsAgentEntries()) {
      return {
        agentsPresent: false,
        error: `App type '${this.appType}' is not supported for agent context`,
        readmeBody: null,
        readmeLegacyFormat: false,
        readmeNote: null,
        readmePath: README_REL,
        latestEntryCountRequested: count,
        entryNumbersIncluded: [],
        entries: [],
      };
    }

    const agentsRootExists = await readTool.checkExists(AGENTS_ROOT);
    if (!agentsRootExists) {
      return {
        agentsPresent: false,
        readmeBody: null,
        readmeLegacyFormat: false,
        readmeNote: "No /agents folder in the workspace yet.",
        readmePath: README_REL,
        latestEntryCountRequested: count,
        entryNumbersIncluded: [],
        entries: [],
        listDirectoryWarning: null,
      };
    }

    let readmeBody = null;
    let readmeLegacyFormat = false;
    let readmeNote = null;
    const readmeExists = await readTool.checkExists(README_REL);
    if (!readmeExists) {
      readmeNote = "agents/README.md not found yet.";
    } else {
      const readmeRead = await readTool.readFile(README_REL, "utf8");
      if (!readmeRead.success) {
        readmeNote =
          readmeRead.message || "Could not read agents/README.md";
      } else {
        const extracted = extractReadmeBody(readmeRead.data.content);
        if (extracted.success) {
          readmeBody = extracted.body;
          readmeLegacyFormat = !!extracted.legacy;
        } else {
          readmeNote = extracted.message;
          readmeBody = readmeRead.data.content.trim();
        }
      }
    }

    const listResult = await listTool.listDirectory(
      AGENTS_ROOT,
      false,
      false,
      false
    );

    const numbers = [];
    if (listResult.success && listResult.data?.entries) {
      for (const e of listResult.data.entries) {
        if (e.type !== "file") {
          continue;
        }
        const m = e.name.match(ENTRY_FILENAME_RE);
        if (m) {
          numbers.push(parseInt(m[1], 10));
        }
      }
    }

    numbers.sort((a, b) => a - b);
    const unique = [...new Set(numbers)];
    const slice =
      count === 0 ? [] : unique.slice(Math.max(0, unique.length - count));

    const entries = [];
    for (const num of slice) {
      const one = await this.readEntry(num);
      if (one.success) {
        entries.push({
          entryNumber: one.data.entryNumber,
          padded: one.data.padded,
          content: one.data.content,
        });
      } else {
        entries.push({
          entryNumber: num,
          padded: this._pad(num),
          content: null,
          error: one.message,
        });
      }
    }

    logger.log(
      { tags: "tools|agent-entries|overview", color1: "cyan" },
      `collectAgentContextForOverview: entries=${entries.length}`
    );

    return {
      agentsPresent: true,
      readmeBody,
      readmeLegacyFormat,
      readmeNote,
      readmePath: README_REL,
      latestEntryCountRequested: count,
      entryNumbersIncluded: slice,
      entries,
      listDirectoryWarning: listResult.success ? null : listResult.message,
    };
  }

  /**
   * Read a single agent entry by number (1–999).
   */
  async readEntry(entryNumber) {
    const padded = this._pad(entryNumber);
    if (!padded) {
      return this.returnError(
        {},
        "entry_number must be an integer from 1 to 999"
      );
    }

    const { readTool } = this._deps();
    if (!readTool) {
      return this.returnError({}, "ReadFileTool is not available");
    }

    if (!this._supportsAgentEntries()) {
      return this.returnError(
        {},
        `App type '${this.appType}' is not supported for agent entries`
      );
    }

    const path = this._entryPath(padded);
    const result = await readTool.readFile(path, "utf8");
    if (!result.success) {
      return this.returnError(
        {
          entryNumber: parseInt(padded, 10),
          padded,
          path,
        },
        result.message || `Could not read agent entry ${padded}`
      );
    }

    return this.returnSuccess(
      {
        entryNumber: parseInt(padded, 10),
        padded,
        path,
        content: result.data.content,
      },
      `Read agent entry ${padded}`
    );
  }

  /**
   * Read inclusive range of agent entries. Span capped at MAX_RANGE_INCLUSIVE.
   */
  async readEntryRange(startNumber, endNumber) {
    let start =
      typeof startNumber === "number"
        ? startNumber
        : parseInt(String(startNumber), 10);
    let end =
      typeof endNumber === "number"
        ? endNumber
        : parseInt(String(endNumber), 10);

    if (!Number.isInteger(start) || !Number.isInteger(end)) {
      return this.returnError(
        {},
        "start and end entry numbers must be integers from 1 to 999"
      );
    }
    if (start < 1 || start > 999 || end < 1 || end > 999) {
      return this.returnError(
        {},
        "Entry numbers must be between 1 and 999 inclusive"
      );
    }
    if (start > end) {
      const t = start;
      start = end;
      end = t;
    }

    const span = end - start + 1;
    if (span > MAX_RANGE_INCLUSIVE) {
      return this.returnError(
        {},
        `Range too large (max ${MAX_RANGE_INCLUSIVE} entries per request). Requested ${span}.`
      );
    }

    const entries = [];
    for (let n = start; n <= end; n += 1) {
      const one = await this.readEntry(n);
      if (one.success) {
        entries.push({
          entryNumber: one.data.entryNumber,
          padded: one.data.padded,
          content: one.data.content,
        });
      } else {
        entries.push({
          entryNumber: n,
          padded: this._pad(n),
          error: one.message,
          content: null,
        });
      }
    }

    return this.returnSuccess(
      {
        startEntryNumber: start,
        endEntryNumber: end,
        count: entries.length,
        entries,
      },
      `Read agent entries ${String(start).padStart(3, "0")}–${String(end).padStart(3, "0")}`
    );
  }

  /**
   * Write or update agent-entry-NNN.md and/or README index line.
   * Pass content (string, including "") to write the entry file; omit/null/undefined to skip.
   * Pass readme_summary to add or update the index line; omit/empty to skip.
   * At least one of content or readme_summary must be provided.
   */
  async writeEntry(entryNumber, content, readmeSummary) {
    const padded = this._pad(entryNumber);
    if (!padded) {
      return this.returnError(
        {},
        "entry_number must be an integer from 1 to 999"
      );
    }

    const hasContent =
      content !== undefined && content !== null && typeof content === "string";
    const summaryOneLine = normalizeSummaryForIndex(readmeSummary);
    const hasSummary = summaryOneLine.length > 0;

    if (!hasContent && !hasSummary) {
      return this.returnError(
        {},
        "Provide content and/or readme_summary: at least one is required (use write-agent-readme for README instructions only)."
      );
    }

    const { readTool, writeTool, createFolderTool } = this._deps();
    if (!readTool || !writeTool || !createFolderTool) {
      return this.returnError(
        {},
        "ReadFileTool, WriteFileTool, or CreateFolderTool is not available"
      );
    }

    if (!this._supportsAgentEntries()) {
      return this.returnError(
        {},
        `App type '${this.appType}' is not supported for agent entries`
      );
    }

    await createFolderTool.createFolder(AGENTS_ROOT, true, true);

    const entryPath = this._entryPath(padded);
    let readmeWritten = false;

    if (hasSummary) {
      const ensured = await this.ensureAgentsReadme();
      if (!ensured.success) {
        return ensured;
      }

      const readmeRead = await readTool.readFile(README_REL, "utf8");
      if (!readmeRead.success) {
        return this.returnError(
          {},
          readmeRead.message || "Could not read agents/README.md"
        );
      }

      const readmeLines = readmeRead.data.content.split(/\r?\n/);
      const patched = patchReadmeIndexLines(readmeLines, padded, summaryOneLine);
      if (!patched.success) {
        return this.returnError({}, patched.message);
      }

      const writeReadmeResult = await writeTool.writeFile(
        README_REL,
        patched.content,
        true,
        "utf8",
        true
      );
      if (!writeReadmeResult.success) {
        return this.returnError(
          { path: README_REL },
          writeReadmeResult.message || "Failed to update agents/README.md"
        );
      }
      readmeWritten = true;
    }

    if (hasContent) {
      const writeEntryResult = await writeTool.writeFile(
        entryPath,
        content,
        true,
        "utf8",
        true
      );
      if (!writeEntryResult.success) {
        return this.returnError(
          { path: entryPath },
          writeEntryResult.message || `Failed to write ${entryPath}`
        );
      }
    }

    logger.log(
      {
        tags: "tools|agent-entries|write",
        color1: "lime",
      },
      `Agent entry ${padded}: entryFile=${hasContent} readmeIndex=${hasSummary}`
    );

    return this.returnSuccess(
      {
        entryNumber: parseInt(padded, 10),
        padded,
        entryPath: hasContent ? entryPath : null,
        readmePath: readmeWritten ? README_REL : null,
        readmeSummary: hasSummary ? summaryOneLine : null,
        wroteEntryFile: hasContent,
        updatedReadmeIndex: readmeWritten,
      },
      hasContent && hasSummary
        ? `Wrote agent entry ${padded} and updated README index`
        : hasContent
          ? `Wrote agent entry ${padded}`
          : `Updated README index for entry ${padded}`
    );
  }
}
